import type { MapLineData, MapSchematicGeometry, MapSchematicPath, MapSchematicPoint, MapSchematicRoadArm } from "./api";

type Vector = { x: number; y: number };

const center: MapSchematicPoint = [500, 500];
const gateDistance = 292;

function vector(from: MapSchematicPoint, to: MapSchematicPoint): Vector {
  return { x: to[0] - from[0], y: to[1] - from[1] };
}

function normalize(value: Vector): Vector {
  const magnitude = Math.hypot(value.x, value.y);
  return magnitude > 0 ? { x: value.x / magnitude, y: value.y / magnitude } : { x: 0, y: -1 };
}

function add(point: MapSchematicPoint, direction: Vector, distance: number): MapSchematicPoint {
  return [point[0] + direction.x * distance, point[1] + direction.y * distance];
}

function right(direction: Vector): Vector {
  return { x: -direction.y, y: direction.x };
}

export function schematicPathKey(entryLineID: string, exitLineID: string) {
  return `${entryLineID}:${exitLineID}`;
}

export function armDirection(arm: MapSchematicRoadArm): Vector {
  return normalize(vector(arm.centerline[0] ?? center, arm.centerline.at(-1) ?? center));
}

export function roadArmForLine(geometry: MapSchematicGeometry, lineID: string) {
  return geometry.arms.find((arm) => arm.line_id === lineID);
}

/**
 * Provides deterministic, editor-only anchors for lines that have not yet been
 * assigned to an OSM arm. The returned map never changes `geometry` and must
 * not be used to decide whether a production flow diagram is complete.
 */
export function schematicLineAnchors(geometry: MapSchematicGeometry, lines: MapLineData[]) {
  const anchors = new Map<string, MapSchematicRoadArm>();
  const assignedArmIDs = new Set<string>();
  geometry.arms.forEach((arm) => {
    if (!arm.line_id) return;
    anchors.set(arm.line_id, arm);
    assignedArmIDs.add(arm.id);
  });
  const remainingLines = lines.filter((line) => !anchors.has(line.id)).sort((left, right) => left.id.localeCompare(right.id));
  const orderedArms = [...geometry.arms].sort((left, right) => left.bearing_degrees - right.bearing_degrees || left.id.localeCompare(right.id));
  const remainingArms = orderedArms.filter((arm) => !assignedArmIDs.has(arm.id));
  const demoArms = remainingArms.length > 0 ? remainingArms : orderedArms;
  remainingLines.forEach((line, index) => {
    const arm = demoArms[index % demoArms.length];
    if (arm) anchors.set(line.id, arm);
  });
  return anchors;
}

export function missingSchematicLineIDs(geometry: MapSchematicGeometry) {
  const assignedLineIDs = new Set(geometry.arms.flatMap((arm) => arm.line_id ? [arm.line_id] : []));
  const requiredLineIDs = new Set(geometry.paths.flatMap((path) => path.mode === "disabled"
    ? []
    : [path.entry_line_id, path.exit_line_id]));
  return Array.from(requiredLineIDs).filter((lineID) => !assignedLineIDs.has(lineID)).sort();
}

export function schematicLaneAnchor(arm: MapSchematicRoadArm, entering: boolean): MapSchematicPoint {
  const outward = armDirection(arm);
  const travel = entering ? { x: -outward.x, y: -outward.y } : outward;
  const laneOffset = Math.min(32, Math.max(12, arm.width_m * 2.4));
  return add(add(center, outward, gateDistance), right(travel), laneOffset);
}

export function schematicGate(arm: MapSchematicRoadArm): [MapSchematicPoint, MapSchematicPoint] {
  const outward = armDirection(arm);
  const midpoint = add(center, outward, gateDistance);
  const normal = right(outward);
  const halfWidth = Math.min(62, Math.max(26, arm.width_m * 5.2));
  return [add(midpoint, normal, -halfWidth), add(midpoint, normal, halfWidth)];
}

function pointText(point: MapSchematicPoint) {
  return `${point[0].toFixed(1)} ${point[1].toFixed(1)}`;
}

export function schematicPathData(geometry: MapSchematicGeometry, path: MapSchematicPath, lineAnchors?: ReadonlyMap<string, MapSchematicRoadArm>) {
  const entry = lineAnchors?.get(path.entry_line_id) ?? roadArmForLine(geometry, path.entry_line_id);
  const exit = lineAnchors?.get(path.exit_line_id) ?? roadArmForLine(geometry, path.exit_line_id);
  if (!entry || !exit || path.mode === "disabled") return "";
  const start = schematicLaneAnchor(entry, true);
  const end = schematicLaneAnchor(exit, false);
  if (path.mode === "manual" && path.waypoints.length > 0) {
    return `M ${pointText(start)} ${path.waypoints.map((point) => `L ${pointText(point)}`).join(" ")} L ${pointText(end)}`;
  }

  const entryDirection = armDirection(entry);
  const exitDirection = armDirection(exit);
  if (entry.id === exit.id) {
    const inward = { x: -entryDirection.x, y: -entryDirection.y };
    const across = right(entryDirection);
    const controlOne = add(add(start, inward, 92), across, 44);
    const controlTwo = add(add(end, inward, 92), across, 44);
    return `M ${pointText(start)} C ${pointText(controlOne)} ${pointText(controlTwo)} ${pointText(end)}`;
  }
  const opposition = entryDirection.x * exitDirection.x + entryDirection.y * exitDirection.y;
  if (opposition < -0.82) return `M ${pointText(start)} L ${pointText(end)}`;
  const controlOne = add(start, { x: -entryDirection.x, y: -entryDirection.y }, 168);
  const controlTwo = add(end, { x: -exitDirection.x, y: -exitDirection.y }, 168);
  return `M ${pointText(start)} C ${pointText(controlOne)} ${pointText(controlTwo)} ${pointText(end)}`;
}

export function clampSchematicPoint(point: MapSchematicPoint): MapSchematicPoint {
  return [Math.min(960, Math.max(40, point[0])), Math.min(960, Math.max(40, point[1]))];
}
